import React, { useState, useEffect, useCallback } from "react";
import {
  FaEdit,
  FaSave,
  FaTimes,
  FaCamera,
  FaBuilding,
  FaMapMarkerAlt,
  FaPhone,
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaEnvelope
} from "react-icons/fa";
import { useProfile, useUpdateOrgProfile } from "../../hooks/useProfile.ts";
import { useVerifyOrganization } from "../../hooks/useUsers.ts";
import { useChangePassword } from "../../hooks/useAuth.ts";
import ChangePasswordModal from "../../components/common/ChangePasswordModal.tsx";
import { useToast } from "../../components/common/ToastContext.tsx";
import { Link } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";

interface ProfileData {
  organization_name: string;
  phone_number: string;
  street: string;
  city: string;
  state: string;
  pincode: string;
  landmark: string;
  address: string;
  profile_picture: File | null;
  verification_requested: boolean;
  [key: string]: any;
}

interface FormErrors {
  organization_name?: string;
  phone_number?: string;
  street?: string;
  city?: string;
  state?: string;
  pincode?: string;
  landmark?: string;
  address?: string;
  profile_picture?: string;
  [key: string]: string | undefined;
}

const OrgProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState<any>();
  const [preview, setPreview] = useState<string>("https://www.shutterstock.com/image-vector/female-cartoon-character-no-face-260nw-2069253944.jpg");
  const { showToast } = useToast();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const [formData, setFormData] = useState<ProfileData>({
    organization_name: "",
    phone_number: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
    landmark: "",
    address: "",
    profile_picture: null,
    verification_requested: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const cred = JSON.parse(localStorage.getItem("user") || "{}");
  const { data: profileData } = useProfile(cred?.user?.id);
  const updateOrgProfileMutation = useUpdateOrgProfile();
  const verifyOrgMutation = useVerifyOrganization();
  const changePasswordMutation = useChangePassword();

  // Validate individual field
  const validateField = (name: string, value: string) => {
    let error = "";
    if (!value.trim()) {
      error = "This field is required";
    } else {
      switch (name) {
        case "phone_number":
          if (value.startsWith('0')) error = "Phone number cannot start with 0";
          else if (!/^\d{10}$/.test(value)) error = "Phone number must be 10 digits";
          break;
        case "pincode":
          if (!/^\d{6}$/.test(value)) error = "Pincode must be 6 digits";
          break;
        case "organization_name":
          if (value.length < 3 || value.length > 50) error = "Organization name must be 3-50 characters";
          break;
        case "city":
        case "state":
          if (value.length < 2 || value.length > 30) error = "Must be 2-30 characters";
          break;
        case "street":
        case "landmark":
        case "address":
          if (value.length < 5 || value.length > 100) error = "Must be 5-100 characters";
          break;
        default:
          break;
      }
    }
    return error;
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    let isValid = true;
    let isComplete = true;

    const requiredFields: (keyof ProfileData)[] = [
      'organization_name', 'phone_number', 'street', 'city', 'state', 'pincode', 'landmark', 'address'
    ];

    requiredFields.forEach(field => {
      const value = formData[field] as string;
      const error = validateField(field as string, value);
      if (error) {
        newErrors[field as string] = error;
        isValid = false;
        isComplete = false;
      }
    });

    if (!formData.profile_picture && preview.includes("shutterstock")) {
      newErrors.profile_picture = "Profile picture is required";
      isValid = false;
      isComplete = false;
    }

    setErrors(newErrors);
    setIsFormValid(isValid);
    setIsProfileComplete(isComplete);
    return isValid;
  };

  useEffect(() => {
    if (profileData?.user?.profile) {
      const data = profileData.user.profile;
      setFormData((prevData: ProfileData) => ({
        organization_name: data?.organization_name || prevData.organization_name || "",
        phone_number: data?.phone_number || prevData.phone_number || "",
        street: data?.street || prevData.street || "",
        city: data?.city || prevData.city || "",
        state: data?.state || prevData.state || "",
        pincode: data?.pincode || prevData.pincode || "",
        landmark: data?.landmark || prevData.landmark || "",
        address: data?.address || prevData.address || "",
        profile_picture: null, // Keep null as file input needs File object, preview handled separately
        verification_requested: data?.verification_requested || false,
      }));
      if (data?.profile_picture) {
        setPreview(`${process.env.REACT_APP_PF}${data.profile_picture}?t=${new Date().getTime()}`);
      }
      setUser(data);

      const requiredFields: (keyof ProfileData)[] = [
        'organization_name', 'phone_number', 'street', 'city', 'state', 'pincode', 'landmark', 'address'
      ];
      const complete = requiredFields.every(field => data[field] && data[field].toString().trim() !== '');
      setIsProfileComplete(complete);
    }
  }, [profileData]);

  useEffect(() => {
    if (isEditing) validateForm();
  }, [formData, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (isEditing) {
      const error = validateField(name, value);
      setErrors((prev: FormErrors) => ({ ...prev, [name]: error }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        showToast("Image size exceeds 5MB limit.", "error");
        return;
      }
      setFormData({ ...formData, profile_picture: file });
      setPreview(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, profile_picture: "" }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showToast("Please fix the errors in the form", "error");
      return;
    }
    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'profile_picture' && formData[key] instanceof File) {
        data.append(key, formData[key] as File);
      } else if (key !== 'profile_picture' && key !== 'verification_requested') {
        data.append(key, formData[key]);
      }
    });

    try {
      await updateOrgProfileMutation.mutateAsync(data);
      setIsEditing(false);
      // Data refresh handled by RQ invalidation
      showToast("Profile updated successfully!", "success");
    } catch (err) {
      console.error("Error saving profile:", err);
      showToast("Failed to update profile.", "error");
    }
  };

  const sendVerificationRequest = async () => {
    if (!isProfileComplete) return showToast("Complete profile before verification", "error");
    if (!user?.documents || user.documents.length < 3) return showToast("Need at least 3 documents", "error");

    try {
      await verifyOrgMutation.mutateAsync({}, {
        onSuccess: (data: any) => {
          // Assuming hook handles toast, but we can add more logic
          if (data?.status === 201 || data?.status === 200) {
            setFormData(prev => ({ ...prev, verification_requested: true }));
          }
        }
      });
    } catch (err) {
      // Error handled by hook
    }
  };

  const documentCount = user?.documents?.length || 0;
  const hasEnoughDocuments = documentCount >= 3;
  const isVerificationPending = formData.verification_requested;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 md:px-8">
      {/* Maximum width container for vertical alignment */}
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col md:flex-row items-center md:items-start gap-8 relative overflow-hidden">
          {/* Profile Image */}
          <div className="relative group shrink-0">
            <div className="w-32 h-32 rounded-full border-4 border-teal-500 shadow-md overflow-hidden">
              <img src={preview} alt="Profile" className="w-full h-full object-cover" />
            </div>
            {isEditing && (
              <label htmlFor="profile_picture"
                className="absolute bottom-0 right-0 p-2.5 rounded-full cursor-pointer bg-teal-600 text-white shadow-lg hover:bg-teal-700 transition-all border-2 border-white">
                <FaCamera size={16} />
                <input type="file" id="profile_picture" className="hidden" onChange={handleImageChange} accept="image/*" />
              </label>
            )}
            {errors.profile_picture && <p className="text-red-500 text-xs mt-2 text-center absolute -bottom-6 w-full">{errors.profile_picture}</p>}
          </div>

          {/* Org Name & Status */}
          <div className="flex-1 w-full text-center md:text-left mt-2 md:mt-0">
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2">
              {isEditing ? (
                <input
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  className="text-3xl font-bold text-gray-800 border-b-2 border-teal-200 focus:border-teal-500 outline-none bg-transparent placeholder-gray-300 w-full"
                  placeholder="Organization Name"
                />
              ) : (
                <h1 className="text-3xl font-bold text-gray-800">{formData.organization_name || "Your Organization"}</h1>
              )}

              {!isEditing && (
                user?.is_verified ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-600 border border-green-200 w-fit mx-auto md:mx-0">
                    <FaCheckCircle size={14} /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-amber-50 text-amber-600 border border-amber-200 w-fit mx-auto md:mx-0">
                    <FaClock size={14} /> Verification Pending
                  </span>
                )
              )}
            </div>
            <p className="text-gray-500 mb-6 flex items-center justify-center md:justify-start gap-2">
              <FaEnvelope className="text-gray-400" /> {cred?.user?.email}
            </p>

            {/* Actions Toolbar */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
              {isEditing ? (
                <>
                  <button onClick={handleSave} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 transition shadow-sm hover:shadow">
                    <FaSave /> Save Changes
                  </button>
                  <button onClick={() => setIsEditing(false)} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-white text-gray-700 border border-gray-300 font-medium hover:bg-gray-50 transition shadow-sm hover:shadow">
                    <FaTimes /> Cancel
                  </button>
                </>
              ) : (
                <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 transition shadow-sm hover:shadow">
                  <FaEdit /> Edit Profile
                </button>
              )}

              {!user?.is_verified && (
                <button
                  onClick={sendVerificationRequest}
                  disabled={isVerificationPending || !isProfileComplete || !hasEnoughDocuments}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition shadow-sm
                            ${isVerificationPending
                      ? 'bg-amber-100 text-amber-700 border border-amber-200 cursor-not-allowed'
                      : (isProfileComplete && hasEnoughDocuments
                        ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow'
                        : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed')
                    }
                        `}
                >
                  {isVerificationPending ? <FaClock /> : <FaCheckCircle />}
                  {isVerificationPending ? "Request Sent" : "Verify Organization"}
                </button>
              )}

              <button onClick={() => setIsPasswordModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-gray-600 font-medium hover:bg-gray-100 transition">
                Change Password
              </button>
            </div>

            {!isProfileComplete && !isEditing && (
              <div className="mt-4 text-sm text-amber-600 bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-center gap-2">
                <FaExclamationTriangle /> Complete all fields to enable verification.
              </div>
            )}
          </div>
        </div>

        {/* DETAILS SECTION - Vertical Stack */}

        {/* Contact info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="p-2 rounded-lg bg-white text-teal-600 shadow-sm"><FaPhone size={14} className="transform rotate-90" /></span>
              Contact Details
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Phone Number</label>
                {isEditing ? (
                  <div>
                    <input
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                    />
                    {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
                  </div>
                ) : (
                  <p className="text-gray-800 font-medium text-lg">{formData.phone_number || "Not Provided"}</p>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Email Address</label>
                <p className="text-gray-800 font-medium text-lg flex items-center gap-2">
                  {cred?.user?.email}
                  <span className="text-green-600 bg-green-50 text-xs px-2 py-0.5 rounded-full border border-green-100">Verified</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Location Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="p-2 rounded-lg bg-white text-teal-600 shadow-sm"><FaMapMarkerAlt size={14} /></span>
              Location Details
            </h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: "Street", name: "street" },
                { label: "City", name: "city" },
                { label: "State", name: "state" },
                { label: "Pincode", name: "pincode" },
              ].map((field) => (
                <div key={field.name}>
                  <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">{field.label}</label>
                  {isEditing ? (
                    <div>
                      <input
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={`Enter ${field.label}`}
                        className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                      />
                      {(errors as any)[field.name] && <p className="text-red-500 text-xs mt-1">{(errors as any)[field.name]}</p>}
                    </div>
                  ) : (
                    <p className="text-gray-800 font-medium">{formData[field.name] || "--"}</p>
                  )}
                </div>
              ))}
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Landmark</label>
              {isEditing ? (
                <div>
                  <input
                    name="landmark"
                    value={formData.landmark}
                    onChange={handleChange}
                    placeholder="Enter landmark"
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                  />
                  {errors.landmark && <p className="text-red-500 text-xs mt-1">{errors.landmark}</p>}
                </div>
              ) : (
                <p className="text-gray-800 font-medium">{formData.landmark || "--"}</p>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 block">Full Address</label>
              {isEditing ? (
                <div>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter full address"
                    rows={2}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition resize-none"
                  />
                  {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                </div>
              ) : (
                <p className="text-gray-800 font-medium leading-relaxed">{formData.address || "--"}</p>
              )}
            </div>
          </div>
        </div>

        {/* Documents Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="p-2 rounded-lg bg-white text-teal-600 shadow-sm"><FaFileAlt size={14} /></span>
              Documents & Verification
            </h3>
            <Link to="/dashboard/documents" className="text-sm font-medium text-teal-600 hover:text-teal-700 hover:underline">
              Manage Documents &rarr;
            </Link>
          </div>
          <div className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="shrink-0 relative">
                {/* Circular Progress (Simplified) */}
                <div className="w-24 h-24 rounded-full border-8 border-gray-100 flex items-center justify-center relative">
                  <span className="text-3xl font-bold text-teal-600">{documentCount}</span>
                  <div className="absolute inset-0 rounded-full border-t-8 border-l-8 border-teal-500 transform -rotate-45 opacity-20"></div>
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <h4 className="text-base font-bold text-gray-800 mb-1">Uploaded Documents</h4>
                <p className={`text-sm mb-4 ${hasEnoughDocuments ? 'text-green-600' : 'text-amber-600'}`}>
                  {hasEnoughDocuments
                    ? <span className="flex items-center justify-center md:justify-start gap-1"><FaCheckCircle /> Requirement Met ({documentCount}/3)</span>
                    : <span className="flex items-center justify-center md:justify-start gap-1"><FaExclamationTriangle /> Please upload at least 3 documents ({documentCount}/3)</span>
                  }
                </p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  To get your organization verified, you must upload valid registration and identity documents.
                  These documents help us ensure trust and safety within the community.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        changePasswordService={(data: any) => changePasswordMutation.mutateAsync(data)}
      />
    </div>
  );
};

export default OrgProfile;